import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  addNewExtra,
  addNewExtraItem,
  deleteExtra,
  deleteExtraItem,
  getListExtraProduct,
  getProductDetail,
  resetDeleteExtraItem,
  resetAddExtraItem,
  resetDeleteExtra,
  resetAddExtra,
} from "../../../../redux/actions/productActions";
import DefaultLayout from "../../../../layouts/DefaultLayout";
import Paginate from "../../../../components/Paginate";
import Alert from "../../../../components/Alert";
import Loader from "../../../../components/Loader";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { toast } from "react-toastify";

function ExtraProductScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  let { id } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [extraId, setExtraId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenViewItems, setIsOpenViewItems] = useState(false);
  const [extraSelect, setExtraSelect] = useState(null);
  const [extraItemId, setExtraItemId] = useState("");
  const [loadEvent, setLoadEvent] = useState(false);

  const [extraName, setExtraName] = useState("");
  const [extraNameError, setExtraNameError] = useState("");

  const [extraOption, setExtraOption] = useState("");
  const [extraOptionError, setExtraOptionError] = useState("");

  const [isRequired, setIsRequired] = useState(false);
  const [nbrItemsRequired, setNbrItemsRequired] = useState(1);
  const [nbrItemsRequiredError, setNbrItemsRequiredError] = useState("");

  const [itemName, setItemName] = useState("");
  const [itemNameError, setItemNameError] = useState("");

  const [itemPrice, setItemPrice] = useState("");
  const [itemPriceError, setItemPriceError] = useState("");

  const [isFree, setIsFree] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const productDetail = useSelector((state) => state.detailProduct);
  const {
    loadingProductInfo,
    errorProductInfo,
    successProductInfo,
    productInfo,
  } = productDetail;

  const listProductExtra = useSelector((state) => state.listExtraProduct);
  const { extras, loadingListExtraProduct, errorListExtraProduct, pages } =
    listProductExtra;

  const extraDelete = useSelector((state) => state.deleteExtra);
  const {
    loadingProductExtraDelete,
    errorProductExtraDelete,
    successProductExtraDelete,
  } = extraDelete;

  const extraCreate = useSelector((state) => state.createNewExtra);
  const {
    loadingProductExtraAdd,
    errorProductExtraAdd,
    successProductExtraAdd,
  } = extraCreate;

  const extraItemCreate = useSelector((state) => state.createNewExtraItem);
  const {
    loadingProductExtraItemAdd,
    errorProductExtraItemAdd,
    successProductExtraItemAdd,
  } = extraItemCreate;

  const redirect = "/";

  // Get the deleteExtraItem state
  const extraItemDelete = useSelector((state) => state.deleteExtraItem);
  const {
    loadingProductExtraItemDelete,
    errorProductExtraItemDelete,
    successProductExtraItemDelete,
  } = extraItemDelete;

  // Store the current extraSelect.id when deleting an item
  const [lastDeletedExtraId, setLastDeletedExtraId] = useState(null);

  // Track if data needs to be refreshed
  const [refreshData, setRefreshData] = useState(false);

  // Track if API calls are in progress
  const [isLoading, setIsLoading] = useState(false);

  // Store the current extraSelect.id when deleting an item
  // We now set lastDeletedExtraId directly in the confirmation modal

  // Initial data loading and auth check
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else if (initialLoadRef.current && !isLoading) {
      // Set loading state
      setIsLoading(true);

      // Mark that we've done the initial load
      initialLoadRef.current = false;

      // Load product detail first
      dispatch(getProductDetail(id));

      // Add a delay before the second API call to prevent simultaneous requests
      setTimeout(() => {
        dispatch(getListExtraProduct(page, id));

        // Reset loading state after a delay to ensure data is loaded
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }, 300);
    }
  }, [navigate, userInfo, dispatch, id, page, isLoading]);

  // Use a ref to track if we need to refresh data
  const needsRefresh = useRef(false);
  const refreshTimeoutRef = useRef(null);

  // Handle data refresh for delete item separately
  useEffect(() => {
    if (successProductExtraItemDelete && !isLoading) {
      // Set loading state
      setIsLoading(true);

      // Load data sequentially
      dispatch(getProductDetail(id));

      setTimeout(() => {
        dispatch(getListExtraProduct(page, id));

        // Handle modal reopening after data is loaded
        setTimeout(() => {
          if (lastDeletedExtraId) {
            const updatedExtra = extras.find(extra => extra.id === lastDeletedExtraId);
            if (updatedExtra) {
              setExtraSelect(updatedExtra);
              // setIsOpenViewItems(true);
            }
            setLastDeletedExtraId(null);
          }

          // Reset the success state to prevent multiple refreshes
          dispatch(resetDeleteExtraItem());

          // Reset loading state
          setIsLoading(false);
        }, 500);
      }, 300);
    }
  }, [successProductExtraItemDelete, isLoading, lastDeletedExtraId, extras, dispatch, id, page]);

  // Handle data refresh for add item separately
  useEffect(() => {
    if (successProductExtraItemAdd && !isLoading) {
      // Set loading state
      setIsLoading(true);

      // Close the add modal and reset form fields
      setIsOpenAdd(false);
      setItemName("");
      setItemPrice("");
      setIsFree(false);

      // Load data sequentially
      dispatch(getProductDetail(id));

      setTimeout(() => {
        dispatch(getListExtraProduct(page, id));

        // Reset the success state to prevent multiple refreshes
        setTimeout(() => {
          dispatch(resetAddExtraItem());

          // Reset loading state
          setIsLoading(false);
        }, 500);
      }, 300);
    }
  }, [successProductExtraItemAdd, isLoading, dispatch, id, page]);

  // Handle data refresh for delete extra separately
  useEffect(() => {
    if (successProductExtraDelete && !isLoading) {
      // Set loading state
      setIsLoading(true);

      // Load data sequentially
      dispatch(getProductDetail(id));

      setTimeout(() => {
        dispatch(getListExtraProduct(page, id));

        // Reset the success state to prevent multiple refreshes
        setTimeout(() => {
          dispatch(resetDeleteExtra());

          // Reset loading state
          setIsLoading(false);
        }, 500);
      }, 300);
    }
  }, [successProductExtraDelete, isLoading, dispatch, id, page]);

  // Handle data refresh for add extra separately
  useEffect(() => {
    if (successProductExtraAdd && !isLoading) {
      // Reset form fields immediately
      setExtraName("");
      setExtraOption("");
      setIsRequired(false);
      setNbrItemsRequired(1);
      setExtraNameError("");
      setExtraOptionError("");
      setNbrItemsRequiredError("");

      // Set loading state
      setIsLoading(true);

      // Load data sequentially
      dispatch(getProductDetail(id));

      setTimeout(() => {
        dispatch(getListExtraProduct(page, id));

        // Reset the success state to prevent multiple refreshes
        setTimeout(() => {
          dispatch(resetAddExtra());

          // Reset loading state
          setIsLoading(false);
        }, 500);
      }, 300);
    }
  }, [successProductExtraAdd, isLoading, dispatch, id, page]);

  // Handle data refresh for other actions
  useEffect(() => {
    // Check if refresh data flag is true
    if (refreshData && !isLoading) {
      // Set the ref to indicate we need a refresh
      needsRefresh.current = true;

      // Reset the refresh flag
      setRefreshData(false);

      // Clear any existing timeout to prevent multiple calls
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Set a new timeout to debounce the refresh
      refreshTimeoutRef.current = setTimeout(() => {
        if (needsRefresh.current && !isLoading) {
          // Set loading state
          setIsLoading(true);

          // Load product detail first
          dispatch(getProductDetail(id));

          // Wait before loading extras to prevent simultaneous requests
          setTimeout(() => {
            dispatch(getListExtraProduct(page, id));

            // Reset loading state and refresh flag
            setTimeout(() => {
              setIsLoading(false);
              needsRefresh.current = false;
            }, 300);
          }, 300);
        }
      }, 500); // Debounce for 500ms
    }

    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [
    refreshData,
    isLoading,
    dispatch,
    id,
    page
  ]);

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <a
                  href="/products"
                  className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200"
                >
                  Products
                </a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Product Extras
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12 flex justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Product Extras
              </h1>
              <p className="text-white/80 max-w-xl">
                Manage additional options and items for your product. Total extras: {extras?.length || 0}
              </p>
            </div>
            {productInfo && (
              <div className="hidden md:flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="flex flex-col">
                  <span className="text-white/70 text-xs">Product:</span>
                  <span className="text-white font-medium">{productInfo?.product_name || "Product"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {/*  */}
        {/* Create Extra Form */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            <h2 className="text-xl font-semibold text-black mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              Create New Extra
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Extra Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                  </svg>
                  Extra Name <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      extraNameError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      extraNameError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="text"
                    placeholder="Enter extra name (e.g., Size, Toppings, Sauce)"
                    value={extraName}
                    onChange={(v) => setExtraName(v.target.value)}
                  />
                  {extraNameError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {extraNameError}
                    </p>
                  )}
                </div>
              </div>

              {/* Extra Option */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                  </svg>
                  Selection Type <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      extraOptionError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      extraOptionError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="text"
                    placeholder="Enter selection type (e.g., Choose one, Multiple choice)"
                    value={extraOption}
                    onChange={(v) => setExtraOption(v.target.value)}
                  />
                  {extraOptionError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {extraOptionError}
                    </p>
                  )}
                </div>
              </div>

              {/* Is Required */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  Is Required
                </label>
                <div className="flex items-center mt-2">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.target.checked)}
                    />
                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    <span className="ms-3 text-sm font-medium text-body">
                      {isRequired ? "Required" : "Optional"}
                    </span>
                  </label>
                </div>
              </div>

              {/* Number of Items Required */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                  </svg>
                  Maximum Items Allowed
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      nbrItemsRequiredError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      nbrItemsRequiredError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="number"
                    min="1"
                    placeholder="Enter maximum number of items allowed"
                    value={nbrItemsRequired}
                    onChange={(v) => setNbrItemsRequired(v.target.value)}
                  />
                  {nbrItemsRequiredError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {nbrItemsRequiredError}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-bodydark">
                    Maximum number of items the customer can select from this extra
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 border-t border-stroke pt-6">
              <div className="flex flex-row items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setExtraName("");
                    setExtraNameError("");
                    setExtraOption("");
                    setExtraOptionError("");
                    setIsRequired(false);
                    setNbrItemsRequired(1);
                    setNbrItemsRequiredError("");
                  }}
                  className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  Reset
                </button>
                <button
                  onClick={() => {
                    // Prevent submitting if already loading
                    if (loadingProductExtraAdd) return;

                    var check = true;
                    setExtraNameError("");
                    setExtraOptionError("");

                    if (extraName === "") {
                      setExtraNameError("This field is required.");
                      check = false;
                    }

                    if (extraOption === "") {
                      setExtraOptionError("This field is required.");
                      check = false;
                    }

                    if (check) {
                      if (nbrItemsRequired === "" || parseInt(nbrItemsRequired) < 1) {
                        setNbrItemsRequiredError("Please enter a valid number (minimum 1).");
                        check = false;
                        toast.error("Please enter a valid maximum number of items.");
                      } else {
                        // Dispatch the add action
                        dispatch(
                          addNewExtra(id, {
                            extra_title: extraName,
                            extra_subtitle: extraOption,
                            is_required: isRequired,
                            nbritems_required: nbrItemsRequired,
                          })
                        );

                        // Note: The form will be reset and data refreshed by the useEffect
                      }
                    } else {
                      toast.error(
                        "Some fields are empty or invalid. Please try again."
                      );
                    }
                  }}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                >
                  {loadingProductExtraAdd ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Create Extra
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/*  */}
        {/* Extras Table */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            <h2 className="text-xl font-semibold text-black mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
              </svg>
              Product Extras List
            </h2>

            {loadingListExtraProduct ? (
              <div className="flex justify-center items-center h-80">
                <Loader />
              </div>
            ) : errorListExtraProduct ? (
              <div className="flex justify-center items-center h-80">
                <Alert message={errorListExtraProduct} />
              </div>
            ) : extras?.length === 0 ? (
              <div className="flex flex-col justify-center items-center h-60 bg-gray-1/30 rounded-lg border border-stroke/50">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-bodydark mb-3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                </svg>
                <p className="text-bodydark text-lg font-medium">No extras found</p>
                <p className="text-bodydark/70 text-sm mt-1">Create your first extra using the form above</p>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-2 text-left">
                      <th className="min-w-[80px] py-4 px-4 font-semibold text-black text-sm rounded-l-lg">
                        #
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Extra Name
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Selection Type
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm">
                        Required
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm">
                        Max Items
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm">
                        Items
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {extras?.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-1 transition-colors duration-200 group ${
                          index === extras.length - 1 ? "" : "border-b border-stroke"
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                            #{item.id}
                          </span>
                        </td>

                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.extra_title ?? "---"}
                          </h5>
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            {item.extra_subtitle ?? "---"}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          {item.is_required ? (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                              Required
                            </div>
                          ) : (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">
                              Optional
                            </div>
                          )}
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                            {item.nbritems_required || 1} items
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {item.extra_items?.length || 0} items
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => {
                                setExtraSelect(item);
                                setIsOpenAdd(true);
                              }}
                              className="hover:text-primary text-body p-1.5 rounded-full hover:bg-primary/10 transition-colors duration-200"
                              title="Add Items"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </button>

                            <button
                              onClick={() => {
                                setExtraSelect(item);
                                setIsOpenViewItems(true);
                              }}
                              className="hover:text-blue-600 text-body p-1.5 rounded-full hover:bg-blue-600/10 transition-colors duration-200"
                              title="View Items"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            </button>

                            <button
                              onClick={() => {
                                setEventType("delete-extra");
                                setExtraId(item.id);
                                setIsOpen(true);
                              }}
                              className="hover:text-danger text-body p-1.5 rounded-full hover:bg-danger/10 transition-colors duration-200"
                              title="Delete Extra"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="mt-6">
                  <Paginate
                    route={`/products/extras/${id}?`}
                    search={""}
                    page={page}
                    pages={pages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 w-full container mt-5"></div>

        {/* Add Extra Item Modal */}
        {isOpenAdd && (
          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
            <div className="bg-white p-6 rounded-xl shadow-xl mx-3 max-w-md w-full border border-stroke animate-fadeIn">
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-stroke">
                <h3 className="text-lg font-bold text-black flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Item to {extraSelect?.extra_title || "Extra"}
                </h3>
                <button
                  onClick={() => setIsOpenAdd(false)}
                  className="text-bodydark hover:text-danger transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mb-4">
                  {extraSelect?.extra_subtitle || "Selection type"}
                </div>

                {/* Item Name */}
                <div className="mb-4">
                  <label className="flex items-center text-sm font-medium text-body mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6Z" />
                    </svg>
                    Item Name <span className="text-danger ml-1">*</span>
                  </label>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      itemNameError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      itemNameError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="text"
                    placeholder="Enter item name (e.g., Small, Medium, Large)"
                    value={itemName}
                    onChange={(v) => setItemName(v.target.value)}
                  />
                  {itemNameError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {itemNameError}
                    </p>
                  )}
                </div>

                {/* Is Free */}
                <div className="mb-4">
                  <label className="flex items-center text-sm font-medium text-body mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    Is Free
                  </label>
                  <div className="flex items-center mt-2">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isFree}
                        onChange={(e) => setIsFree(e.target.checked)}
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      <span className="ms-3 text-sm font-medium text-body">
                        {isFree ? "Free" : "Paid"}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Item Price */}
                <div>
                  <label className="flex items-center text-sm font-medium text-body mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    Item Price {!isFree && <span className="text-danger ml-1">*</span>}
                  </label>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      itemPriceError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      itemPriceError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Enter additional price"
                    value={itemPrice}
                    onChange={(v) => setItemPrice(v.target.value)}
                    disabled={isFree}
                  />
                  {itemPriceError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {itemPriceError}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stroke">
                <button
                  onClick={() => {
                    setIsOpenAdd(false);
                    setItemName("");
                    setItemPrice("");
                    setIsFree(false);
                    setItemNameError("");
                    setItemPriceError("");
                  }}
                  className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Prevent submitting if already loading
                    if (loadingProductExtraItemAdd) return;

                    var check = true;
                    setItemNameError("");
                    setItemPriceError("");

                    if (itemName === "") {
                      setItemNameError("This field is required.");
                      check = false;
                    }

                    // Only validate price if the item is not free
                    if (!isFree && itemPrice === "") {
                      setItemPriceError("This field is required.");
                      check = false;
                    }

                    if (check) {
                      // Dispatch the add action
                      dispatch(
                        addNewExtraItem(extraSelect.id, {
                          item_name: itemName,
                          item_price: isFree ? 0 : itemPrice,
                          is_free: isFree,
                        })
                      );

                      // Note: The form will be reset and modal closed by the useEffect
                    } else {
                      toast.error(
                        "Some fields are empty or invalid. Please try again."
                      );
                    }
                  }}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                >
                  {loadingProductExtraItemAdd ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Add Item
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Items Modal */}
        {isOpenViewItems && extraSelect && (
          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
            <div className="bg-white p-6 rounded-xl shadow-xl mx-3 max-w-2xl w-full border border-stroke animate-fadeIn">
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-stroke">
                <h3 className="text-lg font-bold text-black flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  Items for {extraSelect?.extra_title || "Extra"}
                </h3>
                <button
                  onClick={() => setIsOpenViewItems(false)}
                  className="text-bodydark hover:text-danger transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="mb-4">
                <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mb-4">
                  {extraSelect?.extra_subtitle || "Selection type"}
                </div>

                {extraSelect?.extra_items?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-gray-400 mb-3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                    <p className="text-gray-600 text-lg font-medium">No items found</p>
                    <p className="text-gray-500 text-sm mt-1">Add items using the "Add Items" button</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="bg-gray-2 text-left">
                          <th className="min-w-[80px] py-3 px-4 font-semibold text-black text-sm rounded-l-lg">
                            #
                          </th>
                          <th className="min-w-[180px] py-3 px-4 font-semibold text-black text-sm">
                            Item Name
                          </th>
                          <th className="min-w-[120px] py-3 px-4 font-semibold text-black text-sm">
                            Price
                          </th>
                          <th className="min-w-[100px] py-3 px-4 font-semibold text-black text-sm">
                            Status
                          </th>
                          <th className="min-w-[100px] py-3 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {extraSelect?.extra_items?.map((item, index) => (
                          <tr
                            key={index}
                            className={`hover:bg-gray-1 transition-colors duration-200 group ${
                              index === extraSelect.extra_items.length - 1 ? "" : "border-b border-stroke"
                            }`}
                          >
                            <td className="py-3 px-4">
                              <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                                #{item.id}
                              </span>
                            </td>

                            <td className="py-3 px-4">
                              <h5 className="text-sm font-medium text-black">
                                {item.item_name ?? "---"}
                              </h5>
                            </td>

                            <td className="py-3 px-4">
                              {item.is_free.toString().toLowerCase() === "true" ? (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                                  Free
                                </div>
                              ) : (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                  ${parseFloat(item.item_price).toFixed(2)}
                                </div>
                              )}
                            </td>

                            <td className="py-3 px-4">
                              {item.is_deleted.toString().toLowerCase() === "true" ? (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                                  Deleted
                                </div>
                              ) : (
                                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                                  Active
                                </div>
                              )}
                            </td>

                            <td className="py-3 px-4">
                              <div className="flex items-center justify-center space-x-2">
                                <button
                                  onClick={() => {
                                    setEventType("delete-extra-item");
                                    setExtraItemId(item.id);
                                    setIsOpen(true);
                                  }}
                                  className="hover:text-danger text-body p-1.5 rounded-full hover:bg-danger/10 transition-colors duration-200"
                                  title="Delete Item"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stroke">
                <button
                  onClick={() => {
                    setIsOpenViewItems(false);
                  }}
                  className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
                <button
                  onClick={() => {
                    setIsOpenViewItems(false);
                    setExtraSelect(extraSelect);
                    setIsOpenAdd(true);
                  }}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add New Item
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete-extra"
              ? "Are you sure you want to delete this extra? All associated items will also be deleted."
              : eventType === "delete-extra-item"
              ? "Are you sure you want to delete this item? This action cannot be undone."
              : "Are you sure?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setExtraId("");
              setExtraItemId("");
            } else if (eventType === "delete-extra" && extraId !== "") {
              setLoadEvent(true);
              dispatch(deleteExtra(extraId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else if (eventType === "delete-extra-item" && extraItemId !== "") {
              setLoadEvent(true);
              // Set the lastDeletedExtraId before dispatching the delete action
              if (extraSelect) {
                setLastDeletedExtraId(extraSelect.id);
              }

              // Dispatch the delete action
              dispatch(deleteExtraItem(extraItemId));

              // Close the confirmation modal
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);

              // Close the view items modal temporarily
              // It will be reopened by the useEffect after data refresh
              setIsOpenViewItems(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setExtraId("");
              setExtraItemId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setExtraId("");
            setExtraItemId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default ExtraProductScreen;
